import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, ClipboardCheck, FileDown, LayoutGrid, Loader2, Send, Sparkles, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { AMPEL, COACHING_ZUSTAND, DashboardAnsicht, Quellen, datum } from '@/pages/ErnaehrungPage';
import { MahlzeitKarte, PlanPreis, Rechnung, TagesStand, Tagesleiste, Wochenblick } from '@/pages/ErnaehrungMitgliedPage';
import { Antworten } from '@/pages/ErnaehrungFormulare';
import { KoerperanalyseKarte } from '@/pages/ErnaehrungKoerperanalyse';
import { GenussKarte, genussQuelleCoach } from '@/components/GenussKarte';
import { TagebuchKommentare, tagebuchQuelleCoach } from '@/components/TagebuchKommentare';

/* Ernährung auf der Trainer-Webseite /coach (10.10.2026): Dashboard, Plan, Check-ins mit Feedback.
   Nur mit Schalter „Ernährung“ am Trainer-Zugang; Overviewer sehen alle, Personal Trainer nur ihre Kunden —
   der Server prüft das bei jeder Anfrage. `coach` ist der axios-Client der Trainer-Webseite (eigenes Token).
   Seit 15.10.2026 ist das Ernährungscoaching komplett auf /coach (`CoachErnaehrungBereich`): Diese Datei stellt den
   Start (Dashboard), die Mitgliederliste und — als Reiter „Plan“ und „Formulare“ der Kundenseite — die bisherige
   Mitgliedsansicht. Die Routen hier sind die eigenen Coach-Routen `/api/coach/ernaehrung/*`, nicht der Spiegel. */

const lang = { day: '2-digit', month: '2-digit', year: 'numeric' };
const fehler = (e, rest = 'Das hat nicht geklappt') => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : rest; };
const STATUS = {
  offen: { label: 'offen', cls: 'bg-amber-500/10 text-amber-700 border-amber-200' },
  entwurf: { label: 'Entwurf', cls: 'bg-sky-500/10 text-sky-700 border-sky-200' },      // E5: vom Mitglied begonnen, nicht eingereicht
  verpasst: { label: 'überfällig', cls: 'bg-red-500/10 text-red-700 border-red-200' },
  beantwortet: { label: 'beantwortet', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-200' },
};

/* Mitgliederliste (Coach-Route `/ernaehrung/mitglieder`): Overviewer alle mit Coaching, PT nur seine Kunden. */
export function ErnaehrungMitgliederListe({ coach, onWahl, q = '' }) {
  const [liste, setListe] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    coach.get('/ernaehrung/mitglieder').then((r) => setListe(r.data.mitglieder)).catch((e) => setErr(fehler(e, 'Liste konnte nicht geladen werden')));
  }, [coach]);
  if (err) return <Card><CardContent className="py-8 text-center text-sm text-destructive" role="alert">{err}</CardContent></Card>;
  if (!liste) return <Skeleton className="h-48 w-full rounded-[14px]" />;
  const f = liste.filter((m) => (m.name || '').toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Card data-testid="coach-e-kunden">
      <CardHeader className="pb-2"><CardTitle className="text-base">Kunden im Ernährungscoaching <span className="font-normal text-muted-foreground">· {q ? `${f.length} von ${liste.length}` : liste.length}</span></CardTitle></CardHeader>
      <CardContent className="p-0">
        {!liste.length ? <p className="p-4 text-sm text-muted-foreground">Noch niemand — Coachings bucht man im Shop oder als Zusatz-Abo, zugeordnet wird im CRM.</p>
          : !f.length ? <p className="p-4 text-sm text-muted-foreground">Niemand gefunden.</p> : (
            <ul className="divide-y divide-border/60">
              {f.map((m) => (
                <li key={m.lead_id}>
                  <button type="button" onClick={() => onWahl(m.lead_id)} data-testid={`coach-e-kunde-${m.lead_id}`} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${AMPEL[m.ampel]?.punkt || 'bg-muted'}`} aria-hidden="true" />
                    <span className="font-bold">{m.name || 'Ohne Namen'}</span>
                    <span className="min-w-0 flex-1 text-xs text-muted-foreground">{m.plan ? `Plan ${datum(m.plan.start)}–${datum(m.plan.ende)}` : 'kein Plan'}{m.coaching_ende ? ` · Coaching bis ${datum(m.coaching_ende)}` : ''}</span>
                    {AMPEL[m.ampel] && <Badge variant="outline" className={AMPEL[m.ampel].cls}>{AMPEL[m.ampel].label}</Badge>}
                  </button>
                </li>
              ))}
            </ul>
          )}
      </CardContent>
    </Card>
  );
}

export function ErnaehrungStart({ coach, onWahl }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    coach.get('/ernaehrung/dashboard').then((r) => setD(r.data)).catch((e) => setErr(fehler(e, 'Ernährung konnte nicht geladen werden')));
  }, [coach]);
  if (err) return <Card><CardContent className="py-8 text-center text-sm text-destructive" role="alert">{err}</CardContent></Card>;
  if (!d) return <div className="space-y-3"><Skeleton className="h-24 w-full rounded-[14px]" /><Skeleton className="h-48 w-full rounded-[14px]" /></div>;
  return (
    <div className="space-y-4">
      <DashboardAnsicht d={d} onOeffne={(t) => onWahl(t.lead_id)} />
      <ErnaehrungMitgliederListe coach={coach} onWahl={onWahl} />
    </div>
  );
}

/* Die Daten der Mitgliedsansicht (Coach-Route `/ernaehrung/mitglied/{mid}`): Profil, Plan, Fortschritt, Formulare, Schalter.
   Die Kundenseite lädt sie einmal und reicht sie an die Reiter „Plan“ und „Formulare“ weiter. */
export function useMitgliedDaten(coach, mid) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const laden = useCallback(async () => {
    try { setD((await coach.get(`/ernaehrung/mitglied/${mid}`)).data); setErr(''); }
    catch (e) { setErr(fehler(e, 'Konnte nicht geladen werden')); }
  }, [coach, mid]);
  useEffect(() => { setD(null); laden(); }, [laden]);
  return { d, err, laden };
}

function Feedback({ coach, z, onFertig }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const senden = async () => {
    setBusy(true);
    try { await coach.post(`/ernaehrung/zuweisungen/${z.id}/antwort`, { text }); toast.success('Feedback gesendet — das Mitglied bekommt einen Push'); setText(''); onFertig(); }
    catch (e) { toast.error(fehler(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Textarea aria-label="Feedback an das Mitglied" rows={3} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Kurzes Feedback — kommt per Push in die App" />
      <Button size="sm" disabled={busy || text.trim().length < 2} onClick={senden}>{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Feedback senden</Button>
    </div>
  );
}

async function pdfLaden(coach, mid, art) {
  const { data } = await coach.get(`/ernaehrung/mitglied/${mid}/plan.pdf`, { params: { art }, responseType: 'blob', timeout: 60000 });
  const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url; a.download = art === 'plan' ? 'ernaehrungsplan.pdf' : 'einkaufsliste.pdf';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/* Kopfzeile der Kundenseite: Name, Coaching-Stand, Ampel — aus denselben Daten wie der Plan-Reiter */
export function MitgliedKopf({ d }) {
  const zustand = d?.coaching?.zustand || 'keins';
  return (
    <div className="flex flex-wrap items-center gap-2">
      {COACHING_ZUSTAND[zustand] && <Badge variant="outline" className={COACHING_ZUSTAND[zustand].cls}>Coaching {COACHING_ZUSTAND[zustand].label}</Badge>}
      {d?.fortschritt?.ampel && AMPEL[d.fortschritt.ampel] && <Badge variant="outline" className={AMPEL[d.fortschritt.ampel].cls}>{AMPEL[d.fortschritt.ampel].label}</Badge>}
      {d?.plan && <span className="text-xs text-muted-foreground">Plan {datum(d.plan.start, lang)} – {datum(d.plan.ende, lang)}</span>}
    </div>
  );
}

/* Reiter „Plan“: Ziel und Profil, Körperanalyse (nur lesen), Ernährungsplan (Tag/Woche, PDF, neu erstellen),
   Genuss-Planer und Tagebuch-Kommentare — wie bisher auf /coach. */
export function MitgliedPlan({ coach, mid, d, laden }) {
  const [aktiv, setAktiv] = useState(null);
  const [ansicht, setAnsicht] = useState('tag');
  const [neu, setNeu] = useState({ start: '', tage: '7', vorlage: '' });
  const [busy, setBusy] = useState('');
  const genussQuelle = useMemo(() => genussQuelleCoach(coach, mid), [coach, mid]);
  const tagebuchQuelle = useMemo(() => tagebuchQuelleCoach(coach, mid), [coach, mid]);
  useEffect(() => {
    if (!d?.plan) return;
    setAktiv((a) => (d.plan.tage.some((t) => t.datum === a) ? a : (d.plan.tage.find((t) => t.datum === d.heute) || d.plan.tage[0]).datum));
  }, [d]);
  if (!d) return <Skeleton className="h-64 w-full rounded-[14px]" />;
  const p = d.profil;
  const plan = d.plan;
  const tag = plan?.tage.find((t) => t.datum === aktiv);
  const zustand = d.coaching?.zustand || 'keins';
  const planErzeugen = async () => {
    setBusy('plan_neu');
    try {
      const { data } = await coach.post(`/ernaehrung/mitglied/${mid}/plan`, { start: neu.start || null, tage: Number(neu.tage), vorlage_id: neu.vorlage || null });
      toast.success(`Plan erstellt${data.ersetzt?.length ? ` — ${data.ersetzt.length} Mahlzeiten passend ersetzt` : ''}`); laden();
    } catch (e) { toast.error(fehler(e)); } finally { setBusy(''); }
  };
  const pdf = async (art) => { setBusy(art); try { await pdfLaden(coach, mid, art); } catch (e) { toast.error('PDF konnte nicht erstellt werden'); } finally { setBusy(''); } };
  return (
    <div className="space-y-4" data-testid="coach-e-plan">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Ziel und Profil</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          {/* Dieselbe Tafel wie im CRM (Bedarf 11.10.2026): Der Server rechnet, hier steht jede Zeile */}
          {p.ziele ? <div className="max-w-md"><Rechnung ziele={p.ziele} /></div>
            : <p className="text-muted-foreground">{(typeof p.fehlt === 'string' && p.fehlt) || 'Profil noch nicht ausgefüllt'}. Profil und feste Vorgabe („kcal fest vorgeben“) pflegt das Studio im CRM.</p>}
          {p.gewicht_hinweis && <p className="text-xs text-amber-700" role="status">Im Gewichtsverlauf stehen zuletzt {String(p.gewicht_hinweis.kg).replace('.', ',')} kg, im Profil {String(p.gewicht_hinweis.profil_kg).replace('.', ',')} kg — gerechnet wird mit dem Profilgewicht. Übernehmen kann es das Studio im CRM.</p>}
          {(p.ausschluesse || []).length > 0 && <p>Ernährung: {p.ausschluesse.join(', ').replace(/_/g, ' ')}</p>}
          <p className="text-muted-foreground">{d.fortschritt.trainings_14_tage} Trainings in 14 Tagen{d.fortschritt.gewicht.letzter ? ` · zuletzt ${String(d.fortschritt.gewicht.letzter.kg).replace('.', ',')} kg am ${datum(d.fortschritt.gewicht.letzter.datum)}` : ' · noch kein Gewicht'}{d.fortschritt.gewicht.veraenderung_kg != null ? ` (${d.fortschritt.gewicht.veraenderung_kg > 0 ? '+' : ''}${String(d.fortschritt.gewicht.veraenderung_kg).replace('.', ',')} kg)` : ''}</p>
        </CardContent>
      </Card>

      {/* Körperanalyse (InBody): dieselbe Karte wie im CRM, nur lesen — eintragen und löschen geht im CRM */}
      <KoerperanalyseKarte leadId={mid} ka={d.koerperanalyse} grundumsatzRechnung={p.ziele?.grundumsatz} nurLesen />

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Ernährungsplan</CardTitle>
            {plan && (
              <div className="flex flex-wrap gap-1">
                {[['tag', 'Tag', CalendarDays], ['woche', 'Woche', LayoutGrid]].map(([k, l, I]) => (
                  <Button key={k} size="sm" variant={ansicht === k ? 'secondary' : 'ghost'} onClick={() => setAnsicht(k)}><I className="w-4 h-4 mr-1" />{l}</Button>
                ))}
                <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => pdf('plan')}>{busy === 'plan' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}PDF</Button>
                <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => pdf('einkauf')}>{busy === 'einkauf' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}Einkaufsliste</Button>
              </div>
            )}
          </div>
          {plan && <p className="text-sm text-muted-foreground">{datum(plan.start, lang)} – {datum(plan.ende, lang)} · von {plan.erstellt_von || '–'}{plan.vorlage ? ` · Vorlage „${plan.vorlage.name}“` : ''}</p>}
        </CardHeader>
        <CardContent className="space-y-4">
          {plan && d.plan_hinweis && <p className="rounded-md border border-amber-200 bg-amber-500/5 px-3 py-2 text-sm" role="status">{d.plan_hinweis.text}</p>}
          {plan && ansicht === 'woche' && <><Wochenblick tage={plan.tage} heute={d.heute} onWahl={(x) => { setAktiv(x); setAnsicht('tag'); }} /><PlanPreis plan={plan} /></>}
          {plan && ansicht === 'tag' && tag && (
            <>
              <Tagesleiste tage={plan.tage} aktiv={aktiv} heute={d.heute} onWahl={setAktiv} />
              {tag.hinweis && <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm" role="status">{tag.hinweis}</p>}
              {tag.stand && <TagesStand tag={tag} />}
              <PlanPreis plan={plan} tag={tag} />
              {tag.extra && <p className="text-sm text-muted-foreground">Außerhalb des Plans: {tag.extra.anzahl} Einträge{tag.extra.anzahl ? ` (${tag.extra.summe.kcal} kcal)` : ''} · Wasser {(tag.extra.wasser_ml / 1000).toLocaleString('de-DE')} l · Schritte {tag.extra.schritte.toLocaleString('de-DE')}</p>}
              <div className="grid sm:grid-cols-2 gap-3">
                {tag.mahlzeiten.map((m) => <MahlzeitKarte key={m.slot} m={m} gegessen={(tag.gegessen || []).includes(m.slot)} />)}
              </div>
            </>
          )}
          <div className={`rounded-md border p-3 space-y-2 ${plan ? '' : 'border-primary/40'}`}>
            <p className="text-sm font-medium">{plan ? 'Neuen Plan erstellen' : 'Noch kein Plan — jetzt erstellen'}</p>
            <div className="flex flex-wrap items-end gap-2">
              <FormField label="Start"><Input type="date" className="w-40" value={neu.start} onChange={(e) => setNeu({ ...neu, start: e.target.value })} /></FormField>
              <FormField label="Dauer"><NativeSelect className="w-28" value={neu.tage} onChange={(v) => setNeu({ ...neu, tage: v })} options={[3, 5, 7, 10, 14].map((n) => ({ value: String(n), label: `${n} Tage` }))} /></FormField>
              <FormField label="Grundlage"><NativeSelect className="w-60 max-w-full" value={neu.vorlage} onChange={(v) => setNeu({ ...neu, vorlage: v })} options={[{ value: '', label: 'automatisch aus den Rezepten' }, ...(d.vorlagen || []).map((v) => ({ value: v.id, label: `Vorlage: ${v.name}` }))]} /></FormField>
              <Button disabled={!!busy || !p.ziele} onClick={planErzeugen}>{busy === 'plan_neu' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}Plan erstellen</Button>
            </div>
            <p className="text-xs text-muted-foreground">Start leer = heute. Ein Plan mit demselben Start wird ersetzt. Ohne Tagesziel im Profil geht es nicht — das Profil pflegt das Studio im CRM.</p>
          </div>
          {plan?.datenquellen?.length > 0 && <Quellen bls={plan.datenquellen.some((q) => q.lizenz === 'CC BY 4.0')} off={plan.datenquellen.some((q) => q.name === 'Open Food Facts')} />}
        </CardContent>
      </Card>

      {/* Genuss-Planer (11.10.2026): Wochenkarte und Genussmahlzeiten — nur mit Schalter; der Server prüft den Zugriff je Kunde */}
      {d.funktionen?.genuss_planer && <GenussKarte quelle={genussQuelle} onGeaendert={laden} neuLadenBei={`${plan?.id || ''}|${zustand}`} />}

      {/* Kommentare (11.10.2026, Stufe 2): Tagebuch je Tag mit Kommentar-Verlauf — nur mit Schalter; der Server prüft den Zugriff je Kunde */}
      {(d.funktionen?.kommentare || d.schalter?.essensfoto) && <TagebuchKommentare quelle={tagebuchQuelle} heute={d.heute} />}
    </div>
  );
}

/* Reiter „Formulare“: Check-ins und Formulare mit Antworten, Feedback, Formular zuweisen — wie bisher auf /coach. */
export function MitgliedFormulare({ coach, mid, d, laden }) {
  const [offen, setOffen] = useState(null);
  const [formular, setFormular] = useState('mf_checkin');
  const [busy, setBusy] = useState(false);
  if (!d) return <Skeleton className="h-48 w-full rounded-[14px]" />;
  const zuweisen = async () => {
    setBusy(true);
    try { await coach.post(`/ernaehrung/mitglied/${mid}/formular`, { formular_id: formular }); toast.success('Zugewiesen — die App meldet sich per Push'); laden(); }
    catch (e) { toast.error(fehler(e)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="coach-e-formulare">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="w-4 h-4" aria-hidden="true" />Check-ins & Formulare</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {!(d.formulare || []).length ? <p className="text-sm text-muted-foreground">Noch nichts verschickt.</p> : (
          <ul className="space-y-1.5">
            {d.formulare.map((z) => (
              <li key={z.id} className="rounded-md border">
                <button type="button" aria-expanded={offen === z.id} onClick={() => setOffen(offen === z.id ? null : z.id)} className="w-full text-left px-3 py-2 flex flex-wrap items-center gap-2 text-sm hover:bg-accent/40 rounded-md">
                  <span className="font-medium flex-1 min-w-0">{z.titel}</span>
                  {STATUS[z.status] && <Badge variant="outline" className={STATUS[z.status].cls}>{STATUS[z.status].label}</Badge>}
                  {z.coach_antwort && <Badge variant="secondary" className="font-normal">Feedback</Badge>}
                  <span className="text-xs text-muted-foreground w-full">{z.status === 'beantwortet' ? `beantwortet ${datum(z.beantwortet_am, lang)}` : `fällig ${datum(z.faellig_am, lang)}`}</span>
                </button>
                {offen === z.id && (
                  <div className="border-t px-3 py-3 space-y-3">
                    {z.status === 'beantwortet' ? <Antworten z={z} /> : <p className="text-sm text-muted-foreground">Noch nicht beantwortet.</p>}
                    {z.coach_antwort ? (
                      <div className="rounded-md bg-muted p-2 text-sm"><p className="text-xs text-muted-foreground mb-0.5">Feedback von {z.coach_name} · {datum(z.coach_antwort_am, lang)}</p><p className="whitespace-pre-line">{z.coach_antwort}</p></div>
                    ) : z.status === 'beantwortet' && <Feedback coach={coach} z={z} onFertig={laden} />}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <FormField label="Formular schicken"><NativeSelect className="w-56 max-w-full" value={formular} onChange={setFormular} options={(d.formular_auswahl || []).map((f) => ({ value: f.id, label: f.titel }))} /></FormField>
          <Button size="sm" variant="outline" disabled={busy} onClick={zuweisen}><UserPlus className="w-4 h-4 mr-1" />Zuweisen</Button>
        </div>
      </CardContent>
    </Card>
  );
}
